import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Radio,
  MapPin,
  Users,
} from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STORAGE_KEY_LAST_SHOWN = "workshop_promo_last_shown";
const MODAL_DELAY_MS = 4000; // Exactly 4 seconds after entering site

/**
 * ============================================================
 * GLOBAL WORKSHOP PROMOTIONAL MODAL
 * ============================================================
 * Single global promotional modal for active workshops:
 * - Exactly ONE instance rendered across the entire site
 * - Consolidation of all promoted workshops in a compact carousel
 * - Strict 4-second delay after visitor entry
 * - Once-per-calendar-day visitor frequency capping in localStorage
 * - Suppressed on Admin pages (/admin/*, /admin/login)
 * - Suppressed if no eligible workshops exist
 * - Database-backed dynamic lifecycle CTA (Upcoming -> Register; Live -> Join)
 */
export default function WorkshopPromotionalModal() {
  const location = useLocation();
  const navigate = useNavigate();

  const [workshops, setWorkshops] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef(null);

  // Check if current route is an admin page
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Fetch eligible workshops on mount
  useEffect(() => {
    if (isAdminRoute) return;

    let mounted = true;
    workshopsService
      .getPromotionalWorkshops()
      .then((promoted) => {
        if (!mounted) return;

        // Filter out workshops that this anonymous visitor already registered for in this browser
        const unapplied = (promoted || []).filter((w) => {
          try {
            return localStorage.getItem(`workshop_registered_${w.id}`) !== "true";
          } catch {
            return true;
          }
        });

        setWorkshops(unapplied);
      })
      .catch((err) => {
        console.error("[WorkshopPromotionalModal] Failed to fetch promotional workshops:", err);
      });

    return () => {
      mounted = false;
    };
  }, [isAdminRoute]);

  // Handle frequency capping and 4-second display timer
  useEffect(() => {
    // Suppress if admin, already open, or no eligible workshops
    if (isAdminRoute || isOpen || workshops.length === 0) return;

    // Check once-per-calendar-day frequency cap
    const todayStr = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY_LAST_SHOWN);
      if (lastShown === todayStr) {
        // Already shown today
        return;
      }
    } catch {
      // Ignore localStorage errors
    }

    // Schedule appearance after exactly 4 seconds
    timerRef.current = setTimeout(() => {
      setIsOpen(true);
      try {
        localStorage.setItem(STORAGE_KEY_LAST_SHOWN, todayStr);
      } catch {
        // Ignore localStorage errors
      }
    }, MODAL_DELAY_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [workshops, isAdminRoute, isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePrev = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? workshops.length - 1 : prev - 1));
  }, [workshops.length]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === workshops.length - 1 ? 0 : prev + 1));
  }, [workshops.length]);

  // Keyboard accessibility
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowLeft" && workshops.length > 1) handlePrev();
      if (e.key === "ArrowRight" && workshops.length > 1) handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose, handlePrev, handleNext, workshops.length]);

  if (!isOpen || workshops.length === 0 || isAdminRoute) {
    return null;
  }

  const current = workshops[currentIndex] || workshops[0];
  const isUpcoming = current.is_upcoming || current.lifecycle_stage === "upcoming";

  const handleCtaClick = () => {
    handleClose();
    navigate(`/workshops/${current.slug}/register`);
  };

  const handleViewDetails = () => {
    handleClose();
    navigate(`/workshops/${current.slug}`);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md animate-in fade-in-0 duration-300"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl border border-white/20 bg-gradient-to-b from-slate-900/95 via-black/95 to-black/95 backdrop-blur-2xl shadow-[0_25px_70px_rgba(0,0,0,0.8)] overflow-hidden transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient Top Glow */}
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close promotional announcement"
          className="absolute top-3.5 right-3.5 z-30 p-2 rounded-full bg-black/60 hover:bg-white/20 text-white/80 hover:text-white border border-white/15 backdrop-blur-md transition-all duration-200"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Media Cover Header */}
        <div className="relative aspect-[16/8] w-full overflow-hidden bg-black/80">
          <img
            src={current.cover_image}
            alt={current.title}
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&auto=format&fit=crop&q=80";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />

          {/* Badge Strip on Top */}
          <div className="absolute top-3.5 left-3.5 right-12 flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-primary text-black font-bold text-[10px] tracking-wider uppercase shadow-md shadow-primary/30">
              <Sparkles className="w-3 h-3" />
              <span>Workshop Spotlight</span>
            </span>

            {isUpcoming && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-500/20 text-green-300 border border-green-500/40 font-semibold text-[10px] backdrop-blur-md">
                <span>Upcoming Cohort</span>
              </span>
            )}
          </div>

          {/* Pricing pill on cover */}
          <div className="absolute bottom-3 left-4">
            {current.is_free ? (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/35 backdrop-blur-md text-xs font-bold">
                <span className="text-white/60 line-through text-[11px] font-normal">₹999</span>
                <span className="text-green-400">FREE WORKSHOP</span>
              </div>
            ) : (
              <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/35 backdrop-blur-md text-xs font-bold">
                ₹{current.price?.toLocaleString("en-IN")}
              </span>
            )}
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Title */}
          <div>
            <h3
              id="promo-modal-title"
              className="text-lg sm:text-xl font-bold tracking-tight text-white leading-snug"
            >
              {current.title}
            </h3>
            <p className="text-xs sm:text-sm text-white/70 line-clamp-2 mt-1 leading-relaxed font-normal">
              {current.short_description}
            </p>
          </div>

          {/* Quick Logistics Strip */}
          <div className="grid grid-cols-2 gap-2.5 p-3 rounded-2xl bg-white/[0.03] border border-white/10 text-xs">
            <div className="flex items-center gap-2 text-white/80">
              <Calendar className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="truncate font-semibold">{current.date}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="truncate">
                {current.start_time} - {current.end_time}
              </span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="truncate">{current.mode} Session</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Users className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="truncate">
                {current.spots_remaining > 0
                  ? `${current.spots_remaining} spots remaining`
                  : "Limited Seats"}
              </span>
            </div>
          </div>

          {/* Multiple Workshops Carousel Navigation */}
          {workshops.length > 1 && (
            <div className="flex items-center justify-between pt-1 border-t border-white/10">
              <span className="text-[11px] font-semibold text-white/60">
                Workshop {currentIndex + 1} of {workshops.length}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-1.5 rounded-lg bg-white/[0.06] hover:bg-white/15 text-white border border-white/10 transition-colors"
                  aria-label="Previous workshop"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="p-1.5 rounded-lg bg-white/[0.06] hover:bg-white/15 text-white border border-white/10 transition-colors"
                  aria-label="Next workshop"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-2 flex flex-col sm:flex-row items-center gap-2.5">
            <Button
              onClick={handleCtaClick}
              className="w-full sm:flex-1 h-11 rounded-xl text-xs font-bold gap-2 tracking-wide bg-primary text-black hover:bg-primary/90 shadow-[0_0_20px_rgba(0,194,255,0.4)]"
            >
              <span>Register Now</span>
              <ArrowRight className="w-4 h-4" />
            </Button>

            <Button
              variant="outline"
              onClick={handleViewDetails}
              className="w-full sm:w-auto h-11 px-4 rounded-xl border-white/15 bg-white/[0.04] text-xs font-semibold text-white hover:bg-white/10"
            >
              <span>View Details</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
